// Percorso file: src/pages/legal/LegalDocument.jsx
//
// UN DOCUMENTO LEGALE A SCHERMO (rotta /legal/:slug, cantiere LEGAL-DOCS).
//
// Riceve lo slug dalla rotta, pesca il documento in LEGAL_DOCS
// (legalDocs.js: lì stanno i DATI, qui c'è solo la RESA) e lo rende dentro
// il telaio comune: titolo di pagina, testata (versione, decorrenza,
// stato, ente), poi per ogni sezione un'intestazione <h2> con l'`id`
// dell'ancora e i blocchi del corpo NELL'ORDINE in cui stanno nei dati:
// un blocco `p` è un paragrafo in `.qpsc-body-text`, un blocco `ul` è una
// lista puntata `.qpsc-legal__list` con una voce per elemento (le voci
// riusano `.qpsc-body-text`, così il testo di lista e di paragrafo è lo
// stesso testo). LE LISTE SONO RICORSIVE: una voce è una stringa, oppure
// un oggetto { text, items } se ha sotto-voci — e allora il suo <li>
// porta il testo e, sotto, una sotto-<ul> con la stessa classe, a ogni
// livello (nel riferimento se ne contano fino a quattro). La NUMERAZIONE
// del club (1. / a. / i.) sta DENTRO il testo delle voci, perché il
// documento la usa per i rimandi interni («Where 4[b] occurs»): il
// pallino del browser e il numero convivono («• 1. …»), fedele e
// leggibile; se un giorno desse fastidio, si passa a `list-style: none`
// per le liste numerate — non ora. È la forma delle viste di sistema (components/
// SystemView.jsx: HomeShell + h1.qpsc-page-title + p.qpsc-body-text)
// allungata a un documento a sezioni; le classi che il telaio non ha
// (intestazione di sezione, lista, testata) vivono in legal.css.
//
// La decorrenza (`effectiveDateLabel`) si mostra TALE E QUALE, senza un
// prefisso «Effective»: è già formattata per lo schermo nei dati. Se è
// vuota (documento senza data) non si stampa né lei né il separatore che
// la precede. Lo `status` dei dati NON si mostra: dal cantiere LEGAL-NODRAFT
// (14/9/2026) la riga «Draft — pending legal review» non esiste più.
//
// IL BACK È «DA DOVE SI È VENUTI» (`backTo={() => navigate(-1)}`, come
// MyCalendar.jsx), non `backTo="/home"`: l'instradatore /home, con due o
// più ruoli, riporterebbe a «Choose role» invece che all'elenco appena
// lasciato (Home.jsx). HomeShell accetta una funzione al posto della
// rotta.
//
// SLUG SCONOSCIUTO → messaggio pulito dentro il telaio, non una pagina
// bianca: stesso ramo delle pagine con parametro (Attendance.jsx,
// «Unauthorized access.» in p.qpsc-body-text). La ricerca nella mappa
// passa da `hasOwnProperty` e non da `LEGAL_DOCS[slug]` diretto: con
// l'accesso diretto uno slug che si chiamasse "constructor" leggerebbe dal
// prototipo (stessa cautela di auth/access.js).
//
// Il controllo d'accesso NON sta qui: sta in App.jsx, nel cancello
// condiviso RequireRole. Il testo vive nel bundle, che è scaricabile da
// chiunque: il gate è cortesia di navigazione, non riservatezza.

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import HomeShell from "../../components/HomeShell";
import { LEGAL_DOCS } from "./legalDocs";
// DOPO il telaio, e l'ordine conta: HomeShell porta con sé HomeShell.css.
import "./legal.css";

// Una lista del documento, a qualunque livello: una <ul> con una voce per
// elemento; la voce che ha sotto-voci ({ text, items }) rende il suo testo
// e poi, dentro lo stesso <li>, un'altra <Lista> — stessa classe, stessa
// forma, un livello più in dentro (il rientro lo dà `.qpsc-legal__list`
// stessa, legal.css). La chiave è l'indice: le voci non hanno un id e non
// si riordinano mai.
const Lista = ({ items }) => (
  <ul className="qpsc-legal__list">
    {items.map((voce, j) =>
      typeof voce === "string" ? (
        <li key={j} className="qpsc-body-text">
          {voce}
        </li>
      ) : (
        <li key={j} className="qpsc-body-text">
          {voce.text}
          <Lista items={voce.items} />
        </li>
      )
    )}
  </ul>
);

const LegalDocument = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const indietro = () => navigate(-1);

  const doc = Object.prototype.hasOwnProperty.call(LEGAL_DOCS, slug)
    ? LEGAL_DOCS[slug]
    : null;

  if (!doc) {
    return (
      <HomeShell backTo={indietro}>
        <h1 className="qpsc-page-title">Legal &amp; Privacy</h1>
        <p className="qpsc-body-text">This document is not available.</p>
      </HomeShell>
    );
  }

  return (
    <HomeShell backTo={indietro}>
      <h1 className="qpsc-page-title">{doc.title}</h1>

      <div className="qpsc-legal__meta">
        <p className="qpsc-note">
          Version {doc.version}
          {doc.effectiveDateLabel ? <> &middot; {doc.effectiveDateLabel}</> : null}
        </p>
        <p className="qpsc-note">
          {doc.legalEntity} &middot; {doc.legalEntityId}
        </p>
        <p className="qpsc-note">
          <a className="qpsc-mail-link" href={"mailto:" + doc.contactEmail}>
            {doc.contactEmail}
          </a>
        </p>
      </div>

      {doc.sections.map(({ id, label, body }) => (
        <section key={id} className="qpsc-legal__section">
          <h2 id={id} className="qpsc-legal__section-title">
            {label}
          </h2>
          {body.map((blocco, i) =>
            blocco.type === "ul" ? (
              <Lista key={i} items={blocco.items} />
            ) : (
              <p key={i} className="qpsc-body-text">
                {blocco.text}
              </p>
            )
          )}
        </section>
      ))}
    </HomeShell>
  );
};

export default LegalDocument;
